import * as tslib_1 from "tslib";
import { Module } from "../../module";
import * as dom from '../../dom';
import { comparePoints, DomPosition, getClosestAncestorIn, getContentDocument, getNodeIndex, getNodeLength, getRootContainer, isCharacterDataNode, removeNode, splitDataNode } from '../../dom';
import { 
// from ./util
assertNodeNotReadOnly, assertNoDocTypeNotationEntityAncestor, assertRangeValid, assertValidNodeType, assertValidOffset, beforeAfterNodeTypes, DomRangeBase, getElementAncestor, getRangeDocument, rangeProperties, rootContainerNodeTypes, updateBoundaries, 
// from ./RangeIterator
deleteSubtree, extractSubtree, iterateSubtree, RangeIterator, 
// from ./part1
comparisonConstants, copyComparisonConstants, createDomRangeP1, rangeInspect, rangesEqual, rangeToHtml } from "./_";
import * as log4javascript from "log4javascript";
var log = log4javascript.getLogger("rangy.DomRange");
// Pure JavaScript implementation of DOM Range
var module = new Module("DomRange", ["DomUtil"]);
/*----------------------------------------------------------------------------------------------------------------*/
// Utility functions
function getBoundaryBeforeNode(node) {
    return new DomPosition(node.parentNode, getNodeIndex(node));
}
function getBoundaryAfterNode(node) {
    return new DomPosition(node.parentNode, getNodeIndex(node) + 1);
}
function splitRangeBoundaries(range, positionsToPreserve) {
    assertRangeValid(range);
    log.debug("splitBoundaries called " + range.inspect(), positionsToPreserve);
    var sc = range.startContainer, so = range.startOffset, ec = range.endContainer, eo = range.endOffset;
    var startEndSame = (sc === ec);
    if (isCharacterDataNode(ec) && eo > 0 && eo < ec.length) {
        splitDataNode(ec, eo, positionsToPreserve);
        log.debug("Split end", dom.inspectNode(ec), eo);
    }
    if (isCharacterDataNode(sc) && so > 0 && so < sc.length) {
        log.debug("Splitting start", dom.inspectNode(sc), so);
        sc = splitDataNode(sc, so, positionsToPreserve);
        if (startEndSame) {
            eo -= so;
            ec = sc;
        }
        else if (ec == sc.parentNode && eo >= getNodeIndex(sc)) {
            eo++;
        }
        so = 0;
        log.debug("Split start", dom.inspectNode(sc), so);
    }
    range.setStartAndEnd(sc, so, ec, eo);
    log.debug("splitBoundaries done");
}
/*----------------------------------------------------------------------------------------------------------------*/
function createRangeContentRemover(remover, boundaryUpdater) {
    return function () {
        assertRangeValid(this);
        var sc = this.startContainer, so = this.startOffset, root = this.commonAncestorContainer;
        var iterator = new RangeIterator(this, true);
        // Work out where to position the range after content removal
        var node, boundary;
        if (sc !== root) {
            node = getClosestAncestorIn(sc, root, true);
            boundary = getBoundaryAfterNode(node);
            sc = boundary.node;
            so = boundary.offset;
        }
        // Check none of the range is read-only
        iterateSubtree(iterator, assertNodeNotReadOnly);
        iterator.reset();
        // Remove the content
        var returnValue = remover(iterator);
        iterator.detach();
        // Move to the new position
        boundaryUpdater(this, sc, so, sc, so);
        return returnValue;
    };
}
// https://mariusschulz.com/blog/typescript-2-2-mixin-classes
function createDomRangeP2(Base, boundaryUpdater) {
    function createBeforeAfterNodeSetter(isBefore, isStart) {
        return function (node) {
            assertValidNodeType(node, beforeAfterNodeTypes);
            assertValidNodeType(getRootContainer(node), rootContainerNodeTypes);
            var boundary = (isBefore ? getBoundaryBeforeNode : getBoundaryAfterNode)(node);
            (isStart ? setRangeStart : setRangeEnd)(this, boundary.node, boundary.offset);
        };
    }
    function setRangeStart(range, node, offset) {
        var ec = range.endContainer, eo = range.endOffset;
        if (node !== range.startContainer || offset !== range.startOffset) {
            // Check the root containers of the range and the new boundary, and also check whether the new boundary
            // is after the current end. In either case, collapse the range to the new position
            if (getRootContainer(node) != getRootContainer(ec) || comparePoints(node, offset, ec, eo) == 1) {
                ec = node;
                eo = offset;
            }
            boundaryUpdater(range, node, offset, ec, eo);
        }
    }
    function setRangeEnd(range, node, offset) {
        var sc = range.startContainer, so = range.startOffset;
        if (node !== range.endContainer || offset !== range.endOffset) {
            // Check the root containers of the range and the new boundary, and also check whether the new boundary
            // is after the current end. In either case, collapse the range to the new position
            if (getRootContainer(node) != getRootContainer(sc) || comparePoints(node, offset, sc, so) == -1) {
                sc = node;
                so = offset;
            }
            boundaryUpdater(range, sc, so, node, offset);
        }
    }
    /*
            // Set up inheritance
            var F = function() {};
            F.prototype = api.rangePrototype;
            Base.prototype = new F();
    
            util.extend(Base.prototype, {
               ...
            });
    */
    return /** @class */ (function (_super) {
        tslib_1.__extends(class_1, _super);
        function class_1() {
            var _this = _super !== null && _super.apply(this, arguments) || this;
            _this.setStartBefore = createBeforeAfterNodeSetter(true, true);
            _this.setStartAfter = createBeforeAfterNodeSetter(false, true);
            _this.setEndBefore = createBeforeAfterNodeSetter(true, false);
            _this.setEndAfter = createBeforeAfterNodeSetter(false, false);
            _this.extractContents = createRangeContentRemover(extractSubtree, boundaryUpdater);
            _this.deleteContents = createRangeContentRemover(deleteSubtree, boundaryUpdater);
            return _this;
        }
        class_1.prototype.setStart = function (node, offset) {
            assertNoDocTypeNotationEntityAncestor(node, true);
            assertValidOffset(node, offset);
            setRangeStart(this, node, offset);
        };
        class_1.prototype.setEnd = function (node, offset) {
            assertNoDocTypeNotationEntityAncestor(node, true);
            assertValidOffset(node, offset);
            setRangeEnd(this, node, offset);
        };
        class_1.prototype.setStartAndEnd = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var sc = args[0], so = args[1], ec = sc, eo = so;
            switch (args.length) {
                case 3:
                    eo = args[2];
                    break;
                case 4:
                    ec = args[2];
                    eo = args[3];
                    break;
            }
            assertNoDocTypeNotationEntityAncestor(sc, true);
            assertValidOffset(sc, so);
            assertNoDocTypeNotationEntityAncestor(ec, true);
            assertValidOffset(ec, eo);
            boundaryUpdater(this, sc, so, ec, eo);
        };
        class_1.prototype.setBoundary = function (node, offset, isStart) {
            this["set" + (isStart ? "Start" : "End")](node, offset);
        };
        class_1.prototype.collapse = function (toStart) {
            assertRangeValid(this);
            if (toStart) {
                boundaryUpdater(this, this.startContainer, this.startOffset, this.startContainer, this.startOffset);
            }
            else {
                boundaryUpdater(this, this.endContainer, this.endOffset, this.endContainer, this.endOffset);
            }
        };
        class_1.prototype.selectNodeContents = function (node) {
            assertNoDocTypeNotationEntityAncestor(node, true);
            boundaryUpdater(this, node, 0, node, getNodeLength(node));
        };
        class_1.prototype.selectNode = function (node) {
            assertNoDocTypeNotationEntityAncestor(node, false);
            assertValidNodeType(node, beforeAfterNodeTypes);
            var start = getBoundaryBeforeNode(node), end = getBoundaryAfterNode(node);
            boundaryUpdater(this, start.node, start.offset, end.node, end.offset);
        };
        class_1.prototype.splitBoundaries = function () {
            splitRangeBoundaries(this);
        };
        class_1.prototype.splitBoundariesPreservingPositions = function (positionsToPreserve) {
            splitRangeBoundaries(this, positionsToPreserve);
        };
        class_1.prototype.normalizeBoundaries = function () {
            assertRangeValid(this);
            var sc = this.startContainer, so = this.startOffset, ec = this.endContainer, eo = this.endOffset;
            var mergeForward = function (node) {
                var sibling = node.nextSibling;
                if (sibling && sibling.nodeType == node.nodeType) {
                    ec = node;
                    eo = node.length;
                    node.appendData(sibling.data);
                    removeNode(sibling);
                }
            };
            var mergeBackward = function (node) {
                var sibling = node.previousSibling;
                if (sibling && sibling.nodeType == node.nodeType) {
                    sc = node;
                    var nodeLength = node.length;
                    so = sibling.length;
                    node.insertData(0, sibling.data);
                    removeNode(sibling);
                    if (sc == ec) {
                        eo += so;
                        ec = sc;
                    }
                    else if (ec == node.parentNode) {
                        var nodeIndex = getNodeIndex(node);
                        if (eo == nodeIndex) {
                            ec = node;
                            eo = nodeLength;
                        }
                        else if (eo > nodeIndex) {
                            eo--;
                        }
                    }
                }
            };
            var normalizeStart = true;
            var sibling;
            if (isCharacterDataNode(ec)) {
                if (eo == ec.length) {
                    mergeForward(ec);
                }
                else if (eo == 0) {
                    sibling = ec.previousSibling;
                    if (sibling && sibling.nodeType == ec.nodeType) {
                        eo = sibling.length;
                        if (sc == ec) {
                            normalizeStart = false;
                        }
                        sibling.appendData(ec.data);
                        removeNode(ec);
                        ec = sibling;
                    }
                }
            }
            else {
                if (eo > 0) {
                    var endNode = ec.childNodes[eo - 1];
                    if (endNode && isCharacterDataNode(endNode)) {
                        mergeForward(endNode);
                    }
                }
                normalizeStart = !this.collapsed;
            }
            if (normalizeStart) {
                if (isCharacterDataNode(sc)) {
                    if (so == 0) {
                        mergeBackward(sc);
                    }
                    else if (so == sc.length) {
                        sibling = sc.nextSibling;
                        if (sibling && sibling.nodeType == sc.nodeType) {
                            if (ec == sibling) {
                                ec = sc;
                                eo += sc.length;
                            }
                            sc.appendData(sibling.data);
                            removeNode(sibling);
                        }
                    }
                }
                else {
                    if (so < sc.childNodes.length) {
                        var startNode = sc.childNodes[so];
                        if (startNode && isCharacterDataNode(startNode)) {
                            mergeBackward(startNode);
                        }
                    }
                }
            }
            else {
                sc = ec;
                so = eo;
            }
            boundaryUpdater(this, sc, so, ec, eo);
        };
        class_1.prototype.collapseToPoint = function (node, offset) {
            assertNoDocTypeNotationEntityAncestor(node, true);
            assertValidOffset(node, offset);
            this.setStartAndEnd(node, offset);
        };
        class_1.prototype.parentElement = function () {
            assertRangeValid(this);
            return getElementAncestor(this.commonAncestorContainer, true);
        };
        return class_1;
    }(Base));
}
/*----------------------------------------------------------------------------------------------------------------*/
/** TODO remove comment
 * @requires call `Object.assign(R, comparisonConstants)` after call this function:
 *      `class R extends createPrototypeRange(..`
 * or
 *      `const R = createPrototypeRange(..`
 */
export function createPrototypeRange(Base, boundaryUpdater) {
    // R1 is a not completed class because createDomRangeP1 only implement RangeP1
    // the missing members in RangeP2 will be provided at createDomRangeP2
    // and the missing members in ComparisonConstants will be provided here in `createPrototypeRange`
    // so result `R12` will be a completed class that implement `Range`
    var MixinBase = Base;
    var R1 = createDomRangeP1(MixinBase);
    //after this, instances of R1 will have consts like: START_TO_START,..
    Object.assign(R1.prototype, comparisonConstants);
    var R12 = createDomRangeP2(R1, boundaryUpdater);
    // this is similar to add `static START_TO_START = ..` into R12
    // note that, we can ass static members in subclasses: class A{static s = ..} class B extends A{} B.s
    return Object.assign(R12, comparisonConstants);
}
// TODO remove comment
// we need this const to bypass TS2506: 'DomRange' is referenced directly or indirectly in it own base expression
// export const _DomRange = createPrototypeRange(RangeBase, updateBoundaries);
// export type DomRange = InstanceType<typeof DomRange>;
var DomRange = /** @class */ (function (_super) {
    tslib_1.__extends(DomRange, _super);
    function DomRange() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    DomRange.inspect = rangeInspect;
    DomRange.toHtml = rangeToHtml;
    DomRange.getRangeDocument = getRangeDocument;
    DomRange.rangesEqual = rangesEqual;
    return DomRange;
}(createPrototypeRange(DomRangeBase, updateBoundaries)));
export { DomRange };
// @deprecated pls directly import & use the exported member of this module
Object.assign(DomRange, {
    rangeProperties: rangeProperties,
    RangeIterator: RangeIterator,
    copyComparisonConstants: copyComparisonConstants,
    createPrototypeRange: createPrototypeRange,
});
export function createRangyRange(doc) {
    doc = getContentDocument(doc, module, "createRangyRange");
    return new DomRange(doc);
}
//# sourceMappingURL=part2.js.map